import React, { useCallback, useEffect, useState } from "react";
import { BellOutlined, CheckOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  App,
  Badge,
  Button,
  Empty,
  Input,
  Modal,
  Popover,
  Space,
  Spin,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  InboxService,
  type InboxItem,
  type InboxSummaryResponse,
} from "../../.generated/api-client";
import { apiErrorMessage } from "../services/api";
import {
  businessDateKey,
  formatBusinessMonthDayTime,
} from "../utils/businessTime";
import {
  adminInboxCategoryLabel,
  adminInboxCategoryTone,
  adminRouteForInboxItem,
  ADMIN_INBOX_REFRESH_EVENT,
} from "./adminInboxRouting";
import "./AdminInboxBell.css";
import { VisualLedgerStatusPill } from "./visual-ledger/VisualLedger";
import { useApprovalDetailActions } from "../pages/approvals/useApprovalDetailActions";
import ErrorBoundary from "./ErrorBoundary";

// Keep the shared shell independent of unopened approval detail/table code.
const ApprovalDetailPane = React.lazy(() =>
  import("../pages/approvals/ApprovalDetailPane").then((module) => ({
    default: module.ApprovalDetailPane,
  })),
);

type AdminInboxBellProps = {
  enabled: boolean;
  className?: string;
  variant?: "header" | "owner";
};

export function adminInboxBadgeCount(
  summary:
    | Pick<InboxSummaryResponse, "action_required_count" | "notification_count">
    | null
    | undefined,
): number {
  return (
    (summary?.action_required_count ?? 0) + (summary?.notification_count ?? 0)
  );
}

export function isApprovalInboxItem(
  item: Pick<InboxItem, "category" | "source_id" | "source_table">,
): boolean {
  return (
    item.category === "approval" &&
    ["work_request", "ops.work_request"].includes(item.source_table) &&
    Number(item.source_id) > 0
  );
}

export function adminInboxItemAction(item: InboxItem): {
  label: string;
  clickable: boolean;
} {
  if (isApprovalInboxItem(item)) return { label: "处理", clickable: true };
  const adminRoute = adminRouteForInboxItem(item);
  if (item.requires_action && adminRoute) {
    return { label: "处理", clickable: true };
  }
  if (item.requires_action && !adminRoute) {
    return { label: "暂无网页入口", clickable: false };
  }
  return { label: "查看", clickable: true };
}

function formatCreatedAt(value: string): string {
  const formatted = formatBusinessMonthDayTime(value, "");
  if (!formatted) return "";
  const time = formatted.slice(-5);
  if (businessDateKey(value) === businessDateKey(new Date()))
    return `今天 ${time}`;
  return formatted.replace("-", "/");
}

const AdminInboxBell: React.FC<AdminInboxBellProps> = ({
  enabled,
  className = "",
  variant = "header",
}) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<InboxSummaryResponse | null>(null);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionItem, setDecisionItem] = useState<InboxItem | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setItems([]);
      setError(null);
      setNextCursor(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [summaryResp, itemsResp] = await Promise.all([
        InboxService.getInboxSummaryEndpointApiV1InboxSummaryGet(),
        InboxService.listInboxItemsEndpointApiV1InboxItemsGet({
          limit: 6,
          targetClient: "admin",
        }),
      ]);
      setSummary(summaryResp.data);
      setItems(itemsResp.data.items);
      setNextCursor(itemsResp.data.next_cursor ?? null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    try {
      const response =
        await InboxService.listInboxItemsEndpointApiV1InboxItemsGet({
          limit: 12,
          cursor: nextCursor,
          targetClient: "admin",
        });
      setItems((current) => [...current, ...response.data.items]);
      setNextCursor(response.data.next_cursor ?? null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [loading, nextCursor]);

  const selectedId = decisionItem ? String(decisionItem.source_id) : null;
  const {
    detail,
    detailError,
    detailLoading,
    actionLoading,
    rejectOpen,
    rejectReason,
    setRejectOpen,
    setRejectReason,
    approveSelected,
    rejectSelected,
    loadDetail,
  } = useApprovalDetailActions({
    selectedId,
    message,
    onActionCompleted: async () => {
      setDecisionItem(null);
      await refresh();
    },
  });

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handleRefresh = () => void refresh();
    window.addEventListener(ADMIN_INBOX_REFRESH_EVENT, handleRefresh);
    return () =>
      window.removeEventListener(ADMIN_INBOX_REFRESH_EVENT, handleRefresh);
  }, [refresh]);

  const markRead = useCallback(async (keys: string[]) => {
    if (keys.length === 0) return;
    await InboxService.markInboxReadEndpointApiV1InboxReadPost({
      requestBody: { item_keys: keys },
    });
  }, []);

  const recordInteraction = useCallback(
    (
      item: InboxItem,
      eventType: "user_opened" | "deeplink_failed",
      reason?: string,
    ) => {
      if (item.action_item_id == null) return;
      void InboxService.recordActionItemInteractionEndpointApiV1InboxActionItemsInteractionsPost(
        {
          requestBody: {
            action_item_id: item.action_item_id,
            event_type: eventType,
            reason,
          },
        },
      ).catch(() => undefined);
    },
    [],
  );

  const handleItemClick = useCallback(
    async (item: InboxItem) => {
      try {
        if (!item.is_read) {
          await markRead([item.item_key]);
        }
        recordInteraction(item, "user_opened");
        if (isApprovalInboxItem(item)) {
          setOpen(false);
          setDecisionItem(item);
          void refresh();
          return;
        }
        const route = adminRouteForInboxItem(item);
        if (!route) {
          recordInteraction(
            item,
            "deeplink_failed",
            "missing_or_unregistered_admin_route",
          );
          message.info("这条通知暂无可打开的网页详情。");
          void refresh();
          return;
        }
        setOpen(false);
        navigate(route, {
          state: route.startsWith("/approvals?approvalId=")
            ? { approvalDeepLink: true }
            : undefined,
        });
        void refresh();
      } catch (err) {
        message.warning(apiErrorMessage(err));
      }
    },
    [markRead, message, navigate, recordInteraction, refresh],
  );

  const handleMarkAllRead = useCallback(async () => {
    try {
      await InboxService.markAllInboxNotificationsReadEndpointApiV1InboxNotificationsReadAllPost(
        { requestBody: {} },
      );
      void refresh();
    } catch (err) {
      message.warning(apiErrorMessage(err));
    }
  }, [message, refresh]);

  const content = (
    <div className="admin-inbox-popover">
      <div className="admin-inbox-popover__head">
        <div>
          <Typography.Text strong>待办与通知</Typography.Text>
          <Typography.Text type="secondary">
            {summary
              ? `${summary.action_required_count} 条待处理 · ${summary.notification_count} 条通知`
              : "读取中"}
          </Typography.Text>
        </div>
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void refresh()}
            loading={loading}
          />
          <Button
            type="text"
            size="small"
            icon={<CheckOutlined />}
            disabled={(summary?.notification_count ?? 0) === 0}
            onClick={() => void handleMarkAllRead()}
          >
            全部已读
          </Button>
        </Space>
      </div>

      {error ? (
        <div className="admin-inbox-popover__error">
          <Typography.Text type="danger">{error}</Typography.Text>
          <Button size="small" onClick={() => void refresh()}>
            重试
          </Button>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="admin-inbox-popover__loading">
          <Spin size="small" />
          <Typography.Text type="secondary">正在读取消息…</Typography.Text>
        </div>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无待办与通知"
        />
      ) : (
        <div className="admin-inbox-list">
          {items.map((item) => {
            const action = adminInboxItemAction(item);
            return (
              <button
                key={item.item_key}
                type="button"
                className={`admin-inbox-item ${item.is_read ? "admin-inbox-item--read" : ""}`}
                onClick={() => void handleItemClick(item)}
                disabled={!action.clickable}
              >
                <VisualLedgerStatusPill
                  tone={adminInboxCategoryTone(item.category)}
                >
                  {adminInboxCategoryLabel(item.category)}
                </VisualLedgerStatusPill>
                <span className="admin-inbox-item__body">
                  <span className="admin-inbox-item__title">
                    {item.title}
                    {!item.is_read ? (
                      <span className="admin-inbox-item__dot" />
                    ) : null}
                  </span>
                  {item.subtitle ? (
                    <span className="admin-inbox-item__sub">
                      {item.subtitle}
                    </span>
                  ) : null}
                  <span className="admin-inbox-item__meta">
                    {[item.store_name, formatCreatedAt(item.created_at)]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span
                  className={
                    item.requires_action && action.clickable
                      ? "admin-inbox-item__action"
                      : "admin-inbox-item__action muted"
                  }
                >
                  {action.label}
                </span>
              </button>
            );
          })}
          {nextCursor ? (
            <Button
              block
              type="text"
              loading={loading}
              onClick={() => void loadMore()}
            >
              加载更多
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );

  const count = enabled ? adminInboxBadgeCount(summary) : 0;
  const buttonClassName = [
    "admin-inbox-bell",
    `admin-inbox-bell--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (nextOpen) void refresh();
        }}
        trigger="click"
        placement="bottomRight"
        content={content}
        arrow
      >
        <Badge count={count} overflowCount={99} size="small" offset={[-2, 4]}>
          <button
            type="button"
            className={buttonClassName}
            aria-label={count > 0 ? `${count} 条待办与通知` : "消息中心"}
            disabled={!enabled}
          >
            <BellOutlined />
          </button>
        </Badge>
      </Popover>
      <Modal
        title="审批决策"
        width={560}
        centered
        footer={null}
        rootClassName="vf-ledger-scope"
        className="admin-inbox-modal"
        open={decisionItem !== null}
        closable={!actionLoading}
        mask={{ closable: !actionLoading }}
        keyboard={!actionLoading}
        onCancel={() => {
          if (!actionLoading) setDecisionItem(null);
        }}
        destroyOnHidden
      >
        <ErrorBoundary>
          <React.Suspense fallback={<Spin />}>
            <ApprovalDetailPane
              detail={detail}
              detailError={detailError}
              detailLoading={detailLoading}
              actionLoading={actionLoading}
              listStoreName={decisionItem?.store_name ?? undefined}
              allowActions={decisionItem?.requires_action === true}
              allowReturn={false}
              onBackToList={() => setDecisionItem(null)}
              onReturn={() => undefined}
              onReject={() => setRejectOpen(true)}
              onApprove={() => void approveSelected()}
              onRetry={() => {
                if (selectedId !== null) void loadDetail(Number(selectedId));
              }}
            />
          </React.Suspense>
        </ErrorBoundary>
      </Modal>
      <Modal
        title={`驳回${detail?.typeLabel ?? ""}申请`}
        open={rejectOpen}
        okText="确认驳回"
        okButtonProps={{ danger: true, loading: actionLoading }}
        cancelButtonProps={{ disabled: actionLoading }}
        onOk={() => void rejectSelected()}
        onCancel={() => {
          if (!actionLoading) {
            setRejectOpen(false);
            setRejectReason("");
          }
        }}
      >
        <Typography.Paragraph type="secondary">
          驳回原因会同步给申请人，并写入审批记录。
        </Typography.Paragraph>
        <Input.TextArea
          value={rejectReason}
          maxLength={200}
          rows={4}
          placeholder="请说明驳回原因"
          onChange={(event) => setRejectReason(event.target.value)}
        />
      </Modal>
    </>
  );
};

export default AdminInboxBell;
